import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Grid, FormHelperText } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { popUp, deleteMsg } from "components/Custom/AreYouSurePopUp";
import { UserContext } from "App";
import { DeleteAddressAPI } from "APIs/DevoteeAPIs";
// Receipt page theme (shared)
import FormField from "layouts/receipt/components/FormField";
import { useAccent, ranuOutlinedButtonSx } from "layouts/receipt/components/SectionCard";

// Single source of truth for the address form: EditDevotee imports this for validation.
export const ADDRESS_FIELDS = [
  { name: "title" },
  { name: "phone" },
  { name: "address_line_1", full: true },
  { name: "address_line_2", full: true },
  { name: "landmark" },
  { name: "area" },
  { name: "city", required: true },
  { name: "state" },
  { name: "country" },
  { name: "pincode" },
];

// Format rules, checked only when the field has a value
const VALIDATORS = {
  phone: {
    isValid: (v) => v.length === 10,
    message: "Phone number must be exactly 10 digits",
  },
};

// Digits only, max 10 characters
const sanitizePhone = (value) => `${value ?? ""}`.replace(/\D/g, "").slice(0, 10);

const getFormatError = (name, value) => {
  const rule = VALIDATORS[name];
  return rule && value && !rule.isValid(`${value}`) ? rule.message : "";
};

const EditDevoteeAddress = ({
  devoteeAddress,
  handleDataChange,
  handleDeleteChanges,
  errors,
  onFieldBlur,
}) => {
  const userData = useContext(UserContext);
  const dispatch = useDispatch();
  const { accentName } = useAccent();

  // Blurred fields, so the phone error also shows without relying on the parent
  const [touched, setTouched] = useState({});

  const handleDelete = async (address_id) => {
    const response = await DeleteAddressAPI(address_id, userData.access_token, dispatch);
    if (response !== null) {
      handleDeleteChanges(address_id);
    }
  };

  const handleChange = (index, name, value) =>
    handleDataChange(index, name, name === "phone" ? sanitizePhone(value) : value);

  const handleBlur = (index, name, rowKey) => {
    setTouched((prev) => ({ ...prev, [`${rowKey}:${name}`]: true }));
    onFieldBlur?.(index, name);
  };

  if (!devoteeAddress?.length) {
    return (
      <MDTypography variant="button" color="text" display="block">
        No addresses added yet.
      </MDTypography>
    );
  }

  return (
    <>
      {devoteeAddress.map((address, index) => {
        const rowKey = address.devotee_address_id ?? index;

        return (
          <MDBox
            key={rowKey}
            mb={2}
            p={{ xs: 1.5, md: 2 }}
            sx={({ borders }) => ({
              border: `${borders.borderWidth[1]} solid ${borders.borderColor}`,
              borderRadius: borders.borderRadius.lg,
            })}
          >
            <MDTypography
              variant="button"
              fontWeight="bold"
              color={accentName}
              display="block"
              mb={2}
            >
              {address.title ? address.title : `Address ${index + 1}`}
            </MDTypography>

            <Grid container spacing={2}>
              {ADDRESS_FIELDS.map(({ name, required, full }) => {
                const localError = touched[`${rowKey}:${name}`]
                  ? getFormatError(name, address[name])
                  : "";
                const error = errors[index]?.[name] || localError;

                return (
                  <FormField key={name} name={name} required={required} full={full}>
                    <MDInput
                      fullWidth
                      value={address[name] ?? ""}
                      onChange={(event) => handleChange(index, name, event.target.value)}
                      onBlur={() => handleBlur(index, name, rowKey)}
                      error={!!error}
                      placeholder={`Enter ${name.split("_").join(" ")}`}
                      {...(name === "phone" && {
                        inputProps: { inputMode: "numeric", maxLength: 10 },
                      })}
                    />
                    {error && <FormHelperText error>{error}</FormHelperText>}
                  </FormField>
                );
              })}

              <Grid item xs={12}>
                <MDBox display="flex" justifyContent="flex-end">
                  <MDButton
                    type="button"
                    variant="outlined"
                    color="error"
                    sx={ranuOutlinedButtonSx}
                    onClick={() =>
                      popUp(handleDelete, address.devotee_address_id, deleteMsg("address"))
                    }
                  >
                    Delete
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        );
      })}
    </>
  );
};

EditDevoteeAddress.propTypes = {
  devoteeAddress: PropTypes.arrayOf(PropTypes.object),
  handleDataChange: PropTypes.func.isRequired,
  handleDeleteChanges: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  onFieldBlur: PropTypes.func,
};

EditDevoteeAddress.defaultProps = {
  devoteeAddress: [],
  errors: [],
  onFieldBlur: undefined,
};

export default EditDevoteeAddress;
